// Course Package Import — dialog (Step 1 shell).
// Opens from the existing "Import Course" button. Step 1 wires the dropzone,
// reads the package in the browser, and shows a basic summary. Validation,
// preview and the actual import land in later build steps (PRD section 16).

import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Upload, FileArchive, Download, AlertTriangle, CheckCircle2, Info, X, Loader2, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';
import { readPackage, formatBytes } from './packageReader';
import { validatePackage } from './packageValidate';
import { runImport } from './packageRunner';

const TEMPLATE_URL = '/templates/course-package/course-template.course.zip';
const REPORTS_KEY = 'coursePackageReports'; // last 20 import reports (browser)
const MAX_REPORTS = 20;

function loadReports() {
  try { return JSON.parse(localStorage.getItem(REPORTS_KEY) || '[]'); } catch (e) { return []; }
}
function saveReport(rep) {
  try {
    const entry = {
      batchId: rep.batchId, mode: rep.mode, courseId: rep.courseId,
      packageName: rep.packageName, ok: rep.ok, aborted: rep.aborted || null,
      at: new Date().toISOString(), summary: rep.summary,
      templateVersionBefore: rep.templateVersionBefore, templateVersionAfter: rep.templateVersionAfter,
      forkCount: rep.forkCount, topics: rep.topics, uploads: rep.uploads, blocks: rep.blocks,
    };
    const list = [entry, ...loadReports().filter((r) => r.batchId !== entry.batchId)].slice(0, MAX_REPORTS);
    localStorage.setItem(REPORTS_KEY, JSON.stringify(list));
    return list;
  } catch (e) { return loadReports(); }
}
function downloadReport(rep) {
  const name = `course-import-${rep.batchId || 'report'}.json`;
  const blob = new Blob([JSON.stringify(rep, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

export default function ImportPackageDialog({ open, onOpenChange, orgId, orgName }) {
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState('new');
  const [reading, setReading] = useState(false);
  const [result, setResult] = useState(null); // ReadResult from packageReader
  const [plan, setPlan] = useState(null); // validation plan from packageValidate
  const [dragOver, setDragOver] = useState(false);

  // Add/Replace course picker
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [targetCourseId, setTargetCourseId] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [forksAck, setForksAck] = useState(false);
  const [forkCount, setForkCount] = useState(0);

  // run
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  const [liveItems, setLiveItems] = useState([]);
  const [savedReports, setSavedReports] = useState(() => loadReports());
  const [showHistory, setShowHistory] = useState(false);

  const selectedTemplate = templates.find((c) => c.id === targetCourseId);

  // fork count for Replace warning — count ALL forks (draft + published)
  useEffect(() => {
    if (mode !== 'replace' || !targetCourseId || !orgId) { setForkCount(0); return; }
    let cancelled = false;
    api.get(`/organizations/${orgId}/courses/${targetCourseId}/sync/preview`)
      .then((r) => { if (!cancelled) setForkCount((r.data?.forks || []).length); })
      .catch(() => { if (!cancelled) setForkCount(0); });
    return () => { cancelled = true; };
  }, [mode, targetCourseId, orgId]);

  const reset = useCallback(() => {
    setResult(null);
    setPlan(null);
    setReading(false);
    setMode('new');
    setTargetCourseId('');
    setConfirmTitle('');
    setForksAck(false);
    setForkCount(0);
    setRunning(false);
    setProgress(null);
    setReport(null);
    setLiveItems([]);
    setShowHistory(false);
  }, []);

  useEffect(() => {
    if (!open || mode === 'new' || !orgId) return;
    let cancelled = false;
    setLoadingTemplates(true);
    api.get(`/organizations/${orgId}/courses`, { params: { kind: 'template', limit: 100 } })
      .then((r) => { if (!cancelled) setTemplates(r.data?.courses || []); })
      .catch(() => { if (!cancelled) setTemplates([]); })
      .finally(() => { if (!cancelled) setLoadingTemplates(false); });
    return () => { cancelled = true; };
  }, [open, mode, orgId]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setReading(true);
    setResult(null);
    setPlan(null);
    setReport(null);
    const res = await readPackage(file);
    setResult(res);
    if (res.ok) {
      try { setPlan(validatePackage(res)); }
      catch (e) { setPlan(null); }
    }
    setReading(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleClose = useCallback((next) => {
    if (!next) reset();
    onOpenChange(next);
  }, [onOpenChange, reset]);

  const needsTarget = mode === 'add' || mode === 'replace';
  const replaceReady = mode !== 'replace' || (
    !!selectedTemplate && !selectedTemplate.isPublished
    && confirmTitle.trim() === String(selectedTemplate.title).trim()
    && (forkCount === 0 || forksAck)
  );
  const canImport = !!plan && !plan.fatal && plan.topics.length > 0
    && !running && (!needsTarget || !!targetCourseId) && replaceReady;

  const handleRun = useCallback(async () => {
    setRunning(true);
    setReport(null);
    setProgress(null);
    setLiveItems([]);
    let ledger = {};
    try { ledger = JSON.parse(localStorage.getItem('coursePackageUploads') || '{}'); } catch (e) { ledger = {}; }
    try {
      const rep = await runImport({
        orgId, mode, targetCourseId, confirmTitle, readResult: result, plan, ledger,
        onProgress: (p) => setProgress(p),
        onItem: (it) => setLiveItems((prev) => [...prev, it]),
      });
      setReport(rep);
      try { localStorage.setItem('coursePackageUploads', JSON.stringify(rep.ledger || ledger)); } catch (e) { /* ignore quota */ }
      setSavedReports(saveReport(rep));
      if (rep.aborted) toast.error(rep.aborted);
      else if (rep.ok) toast.success(`Import complete · batch ${rep.batchId}`);
      else toast.warning('Import finished with some items skipped — see the report.');
    } catch (e) {
      toast.error(`Import failed: ${e?.message || 'unknown error'}`);
    } finally {
      setRunning(false);
      setProgress(null);
    }
  }, [orgId, mode, targetCourseId, confirmTitle, result, plan]);

  const PHASE_LABEL = { topics: 'Topics', files: 'Files', blocks: 'Blocks' };
  const progressPct = progress && progress.total > 0
    ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="cpkg-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileArchive className="w-5 h-5 text-indigo-600" />
            Import a course package
          </DialogTitle>
          <DialogDescription>
            Importing into: <span className="font-medium text-gray-700">{orgName || 'your organization'}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Keep-tab-open + rerun-safe notice */}
          <Alert data-testid="cpkg-tab-notice" className="bg-amber-50 border-amber-200">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              Keep this tab open until the import finishes. Running the same package
              again safely skips anything already imported.
            </AlertDescription>
          </Alert>

          {/* Template download */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">New here? Start from the fill-in-the-blanks template.</span>
            <a
              href={TEMPLATE_URL}
              download
              data-testid="cpkg-template-link"
              className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <Download className="w-4 h-4" /> Download the template
            </a>
          </div>

          {/* Mode */}
          <div>
            <Label className="text-sm font-medium">What should happen?</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-2 space-y-1">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="new" id="cpkg-mode-new" data-testid="cpkg-mode-new" />
                <Label htmlFor="cpkg-mode-new" className="font-normal cursor-pointer">Create a new course</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="add" id="cpkg-mode-add" data-testid="cpkg-mode-add" />
                <Label htmlFor="cpkg-mode-add" className="font-normal cursor-pointer">Add to an existing course</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="replace" id="cpkg-mode-replace" data-testid="cpkg-mode-replace" />
                <Label htmlFor="cpkg-mode-replace" className="font-normal cursor-pointer">Replace an existing course's content</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Course picker for Add / Replace — templates only */}
          {needsTarget && (
            <div data-testid="cpkg-target-picker">
              <Label className="text-sm font-medium">Which course?</Label>
              <Select value={targetCourseId} onValueChange={setTargetCourseId}>
                <SelectTrigger className="mt-1" data-testid="cpkg-target-select">
                  <SelectValue placeholder={loadingTemplates ? 'Loading courses…' : 'Choose a template course'} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`cpkg-target-${c.id}`}>
                      {c.title} {c.isPublished ? '· Published' : '· Draft'}
                    </SelectItem>
                  ))}
                  {!loadingTemplates && templates.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-gray-400">No template courses found</div>
                  )}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-gray-400">
                Only template courses are listed. Cohort forks get changes from their template.
              </p>
            </div>
          )}

          {mode === 'replace' && selectedTemplate && (
            <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="cpkg-replace-panel">
              {selectedTemplate.isPublished ? (
                <p className="text-xs text-red-700" data-testid="cpkg-replace-published">
                  This course is Published. Unpublish it first, or use Add. Replace only works on a draft.
                </p>
              ) : (
                <>
                  <p className="text-xs text-red-800">
                    All topics and blocks in <span className="font-medium">{selectedTemplate.title}</span> will be deleted and replaced. Enrolments, cohorts and the course link stay.
                  </p>
                  {forkCount > 0 && (
                    <label className="flex items-start gap-2 text-xs text-red-800" data-testid="cpkg-forks-ack-label">
                      <input type="checkbox" checked={forksAck} onChange={(e) => setForksAck(e.target.checked)} data-testid="cpkg-forks-ack" className="mt-0.5" />
                      <span>This template has {forkCount} fork(s) in cohorts. They keep their current content until an instructor syncs them. Replacing bumps the template version so those forks will show drift.</span>
                    </label>
                  )}
                  <div>
                    <Label className="text-xs text-red-800">Type the course title to confirm</Label>
                    <input
                      type="text"
                      value={confirmTitle}
                      onChange={(e) => setConfirmTitle(e.target.value)}
                      placeholder={selectedTemplate.title}
                      data-testid="cpkg-confirm-title"
                      className="mt-1 w-full rounded border border-red-300 px-2 py-1 text-sm"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Dropzone */}
          <div
            data-testid="cpkg-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              data-testid="cpkg-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Upload className="w-7 h-7 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              {reading ? 'Reading package…' : 'Drop a .course.zip here, or click to choose'}
            </p>
            {result?.packageName && (
              <p className="mt-1 text-xs text-gray-400">{result.packageName}</p>
            )}
          </div>

          {/* Read result */}
          {result && !result.ok && result.error && (
            <Alert variant="destructive" data-testid="cpkg-read-error">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{result.error.message}</AlertDescription>
            </Alert>
          )}

          {result?.ok && plan && (
            <div data-testid="cpkg-preview" className="rounded-lg border border-gray-200 p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-medium">{plan.course.title || 'Untitled course'}</span>
              </div>

              {/* counts */}
              <div className="flex flex-wrap gap-2 text-xs" data-testid="cpkg-counts">
                <Badge variant="outline">{plan.topics.length} topic{plan.topics.length === 1 ? '' : 's'}</Badge>
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{plan.counts.ready} ready</Badge>
                {plan.counts.warning > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{plan.counts.warning} warning</Badge>
                )}
                {plan.counts.error > 0 && (
                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{plan.counts.error} error</Badge>
                )}
                <Badge variant="outline">{plan.counts.files} file{plan.counts.files === 1 ? '' : 's'}</Badge>
                <Badge variant="outline">{formatBytes(plan.counts.totalBytes)}</Badge>
              </div>

              {plan.fatal && (
                <Alert variant="destructive" data-testid="cpkg-fatal">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{plan.fatal.message}</AlertDescription>
                </Alert>
              )}

              {/* course-level messages */}
              {plan.course.messages.length > 0 && (
                <ItemRow testid="cpkg-item-course" status={plan.course.status} label={`Course: ${plan.course.title}`} messages={plan.course.messages} />
              )}

              {/* per-topic / per-block rows */}
              <ScrollArea className="max-h-72 pr-2">
                <div className="space-y-2">
                  {plan.topics.map((t) => (
                    <div key={t.key || t.name} className="space-y-1">
                      <ItemRow
                        testid={`cpkg-item-${t.key}`}
                        status={t.status}
                        label={t.name}
                        kind="Topic"
                        messages={t.messages}
                      />
                      <div className="ml-4 space-y-1">
                        {t.blocks.map((bl) => (
                          <ItemRow
                            key={bl.key}
                            testid={`cpkg-item-${bl.key}`}
                            status={bl.status}
                            label={bl.title}
                            kind={bl.type}
                            messages={bl.messages}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {plan.ignored.length > 0 && (
                <p className="text-xs text-gray-400" data-testid="cpkg-ignored">
                  Ignored ({plan.ignored.length}): {plan.ignored.join(', ')}
                </p>
              )}

              <Alert className="bg-slate-50 border-slate-200">
                <Info className="h-4 w-4 text-slate-500" />
                <AlertDescription className="text-slate-600 text-xs">
                  Import creates the course, its topics, uploads the files
                  (decks, reading PDFs/images, thumbnail, datasets) and builds
                  every block. Progress shows below; a report is saved you can
                  download.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* progress while running: bar + live per-item status */}
          {running && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid="cpkg-progress">
              <div className="flex items-center gap-2 text-sm text-indigo-700">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="font-medium" data-testid="cpkg-progress-label">
                  {progress ? `${PHASE_LABEL[progress.phase] || 'Working'}: ${progress.name || ''}` : 'Preparing course…'}
                </span>
                {progress && progress.total > 0 && (
                  <span className="ml-auto text-xs text-indigo-500">{progress.current}/{progress.total}</span>
                )}
              </div>
              <Progress value={progressPct} className="h-2 bg-indigo-100" data-testid="cpkg-progress-bar" />
              {liveItems.length > 0 && (
                <ScrollArea className="max-h-40 pr-2" data-testid="cpkg-live-items">
                  <div className="space-y-1">
                    {liveItems.slice(-40).map((it, idx) => (
                      <ItemRow
                        key={`live-${idx}`}
                        testid={`cpkg-live-${it.group}-${it.key || idx}`}
                        status={it.status === 'error' || it.status === 'failed' ? 'error' : (['created', 'matched', 'uploaded'].includes(it.status) ? 'ready' : 'warning')}
                        kind={it.type || it.kind || (it.group === 'topic' ? 'Topic' : '')}
                        label={`${it.title || it.name || it.key || ''} — ${it.status}`}
                      />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}

          {/* report after running */}
          {report && (
            <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid="cpkg-report">
              {report.aborted ? (
                <Alert variant="destructive" data-testid="cpkg-report-aborted">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{report.aborted}</AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    {report.ok
                      ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                      : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                    <span className="font-medium">{report.ok ? 'Import complete' : 'Import finished with issues'}</span>
                    <Button
                      variant="outline" size="sm" className="ml-auto h-7 text-xs"
                      data-testid="cpkg-download-report"
                      onClick={() => downloadReport(report)}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" /> Download report
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500" data-testid="cpkg-report-batch">Batch {report.batchId} · course {report.courseId}</p>
                  {(report.templateVersionBefore != null || report.templateVersionAfter != null) && (
                    <p className="text-xs text-gray-500" data-testid="cpkg-report-version">
                      Template version: {report.templateVersionBefore ?? '—'} → {report.templateVersionAfter ?? '—'}
                      {report.mode === 'replace' && ` · ${report.forkCount || 0} fork(s) keep their content until synced`}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs" data-testid="cpkg-report-summary">
                    <Badge variant="outline">{report.summary.topicsCreated} topics created</Badge>
                    {report.summary.topicsMatched > 0 && <Badge variant="outline">{report.summary.topicsMatched} matched</Badge>}
                    {report.summary.topicsFailed > 0 && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{report.summary.topicsFailed} topics failed</Badge>}
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{report.summary.blocksCreated} blocks created</Badge>
                    {report.summary.blocksSkipped > 0 && <Badge variant="outline">{report.summary.blocksSkipped} skipped</Badge>}
                    {report.summary.blocksNotCreated > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{report.summary.blocksNotCreated} not created</Badge>}
                    {report.summary.blocksFailed > 0 && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{report.summary.blocksFailed} blocks failed</Badge>}
                  </div>
                  {/* file uploads summary */}
                  <div className="flex flex-wrap gap-2 text-xs" data-testid="cpkg-report-files">
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">{report.summary.filesUploaded} files uploaded</Badge>
                    {report.summary.filesSkipped > 0 && <Badge variant="outline">{report.summary.filesSkipped} already uploaded</Badge>}
                    {report.summary.filesRefused > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{report.summary.filesRefused} refused</Badge>}
                    {report.summary.filesFailed > 0 && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{report.summary.filesFailed} failed</Badge>}
                  </div>
                  <ScrollArea className="max-h-48 pr-2">
                    <div className="space-y-1">
                      {report.topics.map((t) => (
                        <ItemRow
                          key={t.key || t.name}
                          testid={`cpkg-report-topic-${t.key}`}
                          status={t.status === 'error' ? 'error' : (t.status === 'skipped' ? 'warning' : 'ready')}
                          kind="Topic"
                          label={`${t.name} — ${t.message}`}
                        />
                      ))}
                      {(report.uploads || []).map((u, idx) => (
                        <ItemRow
                          key={`u-${idx}`}
                          testid={`cpkg-report-upload-${u.key}-${idx}`}
                          status={u.status === 'failed' ? 'error' : (u.status === 'refused' ? 'warning' : 'ready')}
                          kind={u.kind}
                          label={`${u.key} — ${u.status}: ${u.message}`}
                        />
                      ))}
                      {(report.blocks || []).map((bl, idx) => (
                        <ItemRow
                          key={`b-${idx}`}
                          testid={`cpkg-report-block-${bl.key}`}
                          status={bl.status === 'error' ? 'error' : (bl.status === 'created' ? 'ready' : 'warning')}
                          kind={bl.type}
                          label={`${bl.title || bl.key} — ${bl.status}: ${bl.message}`}
                        />
                      ))}
                    </div>
                  </ScrollArea>
                </>
              )}
            </div>
          )}

          {/* Recent reports kept in the browser (last 20) */}
          {savedReports.length > 0 && !running && (
            <div className="rounded-lg border border-gray-200 p-3" data-testid="cpkg-history">
              <button
                type="button"
                className="flex w-full items-center gap-2 text-sm font-medium text-gray-700"
                onClick={() => setShowHistory((v) => !v)}
                data-testid="cpkg-history-toggle"
              >
                <History className="w-4 h-4 text-gray-500" />
                Recent imports ({savedReports.length})
                <span className="ml-auto text-xs text-indigo-600">{showHistory ? 'Hide' : 'Show'}</span>
              </button>
              {showHistory && (
                <ScrollArea className="mt-2 max-h-56 pr-2">
                  <div className="space-y-1">
                    {savedReports.map((r) => (
                      <div key={r.batchId} className="flex items-center gap-2 text-xs" data-testid={`cpkg-history-${r.batchId}`}>
                        {r.aborted ? <X className="w-3.5 h-3.5 text-red-600 shrink-0" />
                          : r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                            : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                        <span className="truncate text-gray-700">
                          {r.packageName || r.batchId} · {r.mode} · {new Date(r.at).toLocaleString()}
                        </span>
                        <Button
                          variant="ghost" size="sm" className="ml-auto h-6 px-2 text-xs"
                          data-testid={`cpkg-history-download-${r.batchId}`}
                          onClick={() => downloadReport(r)}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="cpkg-cancel-btn">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
          <Button
            disabled={!canImport}
            data-testid="cpkg-import-btn"
            title={canImport ? 'Import the course package' : (mode === 'replace' ? 'Pick a draft template, type its title, and tick the fork notice' : 'Fix the errors above, pick a target, or choose a package with topics')}
            onClick={handleRun}
          >
            {running ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importing…</>) : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const STATUS_ICON = {
  ready: <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />,
  error: <X className="w-4 h-4 text-red-600 shrink-0" />,
};

function ItemRow({ testid, status, label, kind, messages = [] }) {
  return (
    <div className="text-xs" data-testid={testid}>
      <div className="flex items-center gap-2">
        {STATUS_ICON[status] || STATUS_ICON.ready}
        <span data-testid={`${testid}-status`} className="sr-only">{status}</span>
        {kind && <Badge variant="outline" className="text-[10px] py-0">{kind}</Badge>}
        <span className="text-gray-700 truncate">{label}</span>
      </div>
      {messages.length > 0 && (
        <ul className="ml-6 mt-0.5 space-y-0.5">
          {messages.map((m, i) => (
            <li key={i} className={m.level === 'error' ? 'text-red-600' : (m.level === 'warning' ? 'text-amber-600' : 'text-gray-500')}>
              {m.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
